import { Component } from '@theme/component';

/**
 * Canonical event name the built-in bra size calculator (assets/afs-calculator.js)
 * listens for. Kept in sync with that file, NOT with the block's translatable
 * `event_name` setting.
 */
const CANONICAL_EVENT = 'size-guide:calculadora';

/**
 * A trigger button that fires a custom event so an external development
 * (e.g. a "find my size" calculator built by another team) can hook into it
 * without this theme owning that logic.
 *
 * Integration contract for the external developer:
 *   - Listen on `document` for the event named in `data-event-name`
 *     (defaults to "size-guide:calculadora").
 *   - The event bubbles and carries `detail` with:
 *       { blockId, productId, variantId, trigger }
 *     where `trigger` is the clicked button element.
 *   - Alternatively, hook the button by its configured `id`, extra class,
 *     or `data-*` attributes rendered from the block settings.
 *
 * @extends Component
 */
class SizeGuideTrigger extends Component {
  /**
   * Dispatches the configured custom event.
   *
   * @param {MouseEvent} event - The click event proxied by the theme's event system.
   */
  fire(event) {
    const target = event.target instanceof HTMLElement ? event.target : this;
    const button = target.closest('button') ?? this;
    const eventName = this.getAttribute('data-event-name') || CANONICAL_EVENT;

    const detail = {
      blockId: this.getAttribute('data-block-id') || null,
      productId: this.getAttribute('data-product-id') || null,
      variantId: this.getAttribute('data-variant-id') || null,
      trigger: button,
    };

    // Always fire the canonical event name alongside any configured one. The
    // block's `event_name` setting is a translatable text field, so a
    // per-locale content translation (or auto-translation) can silently change
    // it — "size-guide:calculadora" became "size-guide:calculator" in English —
    // and desync it from the listener, whose name comes from a non-translatable
    // Liquid default. Emitting the canonical name too keeps the built-in
    // calculator working no matter what the setting was translated to.
    const names = eventName === CANONICAL_EVENT ? [eventName] : [eventName, CANONICAL_EVENT];
    for (const name of names) {
      this.dispatchEvent(new CustomEvent(name, { bubbles: true, composed: true, detail }));
    }
  }
}

if (!customElements.get('size-guide-trigger')) {
  customElements.define('size-guide-trigger', SizeGuideTrigger);
}
